import { cn } from '@/lib/utils';
import { MultiSelect } from '@/components/ui/MultiSelect';
import {
  activeChips,
  filterCount,
  type CashFlowFilters,
  type FacetGroup,
  type FilterKey,
} from '@/lib/cashFlowFilters';

/**
 * The faceted filter for the cash-flow primary view (docs/UI_STANDARDS — one filter
 * pattern, reused). Rendered INLINE as a full-width region directly below the shared
 * toolbar (never an absolutely-positioned popover, which clipped behind the app
 * sidebar). Presentational + controlled: it renders the facets and reports toggles;
 * the active selection is LIFTED to CashFlowView so every lens (table / calendar /
 * ledger / KPIs) reads the same filter (master rule: shared state, not per-component).
 * Each option shows a live count from the current window so the user sees what a
 * facet will narrow to.
 */
type Counts = Partial<Record<FilterKey, Record<string, number>>>;

export interface FilterPanelProps {
  facets: FacetGroup[];
  filters: CashFlowFilters;
  counts: Counts;
  onToggle: (key: FilterKey, value: string) => void;
  onAmount: (which: 'min' | 'max', value: string) => void;
  onClear: () => void;
  onClose: () => void;
}

export function FilterPanel({ facets, filters, counts, onToggle, onAmount, onClear, onClose }: FilterPanelProps) {
  const total = filterCount(filters);
  return (
    <section
      id="cashflow-filter-panel"
      role="region"
      aria-label="Filtrid"
      className="rounded-md border border-border bg-card px-2.5 py-2 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {facets.map((g) =>
          // Few-option facets (Suund, Osapool) stay inline chips; many-option facets
          // collapse into a single multi-select slot so the whole bar fits ≤ 2 rows.
          g.options.length > 0 && g.options.length <= 2 ? (
            <InlineFacet key={g.key} group={g} filters={filters} counts={counts} onToggle={onToggle} />
          ) : (
            <MultiSelect
              key={g.key}
              label={g.label}
              options={g.options.map((o) => ({ ...o, count: counts[g.key]?.[o.value] ?? 0 }))}
              selected={filters.sel[g.key]}
              onToggle={(value) => onToggle(g.key, value)}
            />
          ),
        )}
        <AmountRange filters={filters} onAmount={onAmount} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">{total} filtrit</span>
          <button type="button" onClick={onClear} className="text-[11px] text-primary hover:underline">
            Tühjenda kõik
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Valmis
          </button>
        </div>
      </div>
    </section>
  );
}

/** A ≤ 2-option facet rendered as inline toggle chips (Suund / Osapool). */
function InlineFacet({
  group,
  filters,
  counts,
  onToggle,
}: {
  group: FacetGroup;
  filters: CashFlowFilters;
  counts: Counts;
  onToggle: (key: FilterKey, value: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{group.label}</span>
      {group.options.map((o) => {
        const on = filters.sel[group.key].has(o.value);
        const count = counts[group.key]?.[o.value] ?? 0;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            aria-label={`${group.label}: ${o.label} (${count})`}
            onClick={() => onToggle(group.key, o.value)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
              on
                ? 'border-primary bg-primary/15 text-foreground'
                : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {o.label}
            <span className="tabular-nums text-[10px] text-muted-foreground">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The inclusive gross-amount range (€) — a filter threshold, never a money calc. */
function AmountRange({
  filters,
  onAmount,
}: {
  filters: CashFlowFilters;
  onAmount: (which: 'min' | 'max', value: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Summa €</span>
      <input
        inputMode="numeric"
        aria-label="Summa alates (€)"
        value={filters.amountMin}
        onChange={(e) => onAmount('min', e.target.value)}
        placeholder="min"
        className="w-16 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground placeholder:text-muted-foreground"
      />
      <span className="text-muted-foreground">–</span>
      <input
        inputMode="numeric"
        aria-label="Summa kuni (€)"
        value={filters.amountMax}
        onChange={(e) => onAmount('max', e.target.value)}
        placeholder="max"
        className="w-16 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-foreground placeholder:text-muted-foreground"
      />
    </div>
  );
}

export interface FilterChipBarProps {
  facets: FacetGroup[];
  filters: CashFlowFilters;
  onToggle: (key: FilterKey, value: string) => void;
  onAmount: (which: 'min' | 'max', value: string) => void;
  onClear: () => void;
}

/** The removable active-filter chips shown under the toolbar. */
export function FilterChipBar({ facets, filters, onToggle, onAmount, onClear }: FilterChipBarProps) {
  const chips = activeChips(filters, facets);
  const hasAmount = Boolean(filters.amountMin.trim() || filters.amountMax.trim());
  if (chips.length === 0 && !hasAmount) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Filtrid:</span>
      {chips.map((c) => (
        <span
          key={c.id}
          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] text-foreground"
        >
          {c.label}
          <button
            type="button"
            aria-label={`Eemalda ${c.label}`}
            onClick={() => onToggle(c.key, c.value)}
            className="text-foreground/50 hover:text-foreground"
          >
            ×
          </button>
        </span>
      ))}
      {hasAmount && (
        <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] text-foreground">
          {`${filters.amountMin.trim() || '0'}–${filters.amountMax.trim() || '∞'} €`}
          <button
            type="button"
            aria-label="Eemalda summa vahemik"
            onClick={() => {
              onAmount('min', '');
              onAmount('max', '');
            }}
            className="text-foreground/50 hover:text-foreground"
          >
            ×
          </button>
        </span>
      )}
      <button type="button" onClick={onClear} className="text-[11px] text-primary hover:underline">
        Tühjenda kõik
      </button>
    </div>
  );
}
